import type { County, JurisdictionName } from './r407-geo';

/*
 * 36 hand-written synthetic lead rows for the radar407 digest mock.
 * Real production CSVs carry contact details and are never touched. Street names are real
 * Central Florida arterials, house numbers are random, there are no company names at all and
 * every license number is masked. What is true is the distribution: the trade mix, the county
 * mix (roughly Orange 55 / Seminole 22 / Osceola 23), the one-in-three unresolved rate and
 * the NOC lead times around the real 38-day median. Sorted newest first.
 */

export type Trade = 'ROOFING' | 'HVAC' | 'ELECTRICAL' | 'PLUMBING' | 'POOL';

export const R407_TRADES: Trade[] = ['ROOFING', 'HVAC', 'ELECTRICAL', 'PLUMBING', 'POOL'];

export interface LeadSample {
  /* Display-ready, e.g. 'AUG 31'. */
  date: string;
  address: string;
  jurisdiction: JurisdictionName;
  county: County;
  trade: Trade;
  type: string;
  valueUsd: number;
  /* Whether the permit resolved to a state license. About one in three does not. */
  resolved: boolean;
  /* Masked DBPR-style number, e.g. 'CCC13xxxxx'. Never a full license. */
  licenseMasked?: string;
  /* Days the notice of commencement landed before the permit, when one exists. */
  nocDaysBefore?: number;
}

const MASK: Record<Trade, string> = {
  ROOFING: 'CCC13xxxxx',
  HVAC: 'CAC18xxxxx',
  ELECTRICAL: 'EC13xxxxxx',
  PLUMBING: 'CFC14xxxxx',
  POOL: 'CPC14xxxxx',
};

interface Seed {
  date: string;
  address: string;
  jurisdiction: JurisdictionName;
  county: County;
  trade: Trade;
  type: string;
  valueUsd: number;
  resolved: boolean;
  nocDaysBefore?: number;
}

const seeds: Seed[] = [
  /* AUG 31 */
  {
    date: 'AUG 31',
    address: '2411 SILVER STAR RD',
    jurisdiction: 'Ocoee',
    county: 'Orange',
    trade: 'ROOFING',
    type: 'ROOF REPLACEMENT',
    valueUsd: 18400,
    resolved: true,
    nocDaysBefore: 41,
  },
  {
    date: 'AUG 31',
    address: '1187 CURRY FORD RD',
    jurisdiction: 'Orlando',
    county: 'Orange',
    trade: 'ROOFING',
    type: 'RE-ROOF, SHINGLE',
    valueUsd: 14250,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '902 RED BUG LAKE RD',
    jurisdiction: 'Casselberry',
    county: 'Seminole',
    trade: 'ROOFING',
    type: 'ROOF REPLACEMENT',
    valueUsd: 16700,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '6420 PLEASANT HILL RD',
    jurisdiction: 'Osceola County',
    county: 'Osceola',
    trade: 'ROOFING',
    type: 'RE-ROOF, SHINGLE',
    valueUsd: 15300,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '7815 SAND LAKE RD',
    jurisdiction: 'Orange County',
    county: 'Orange',
    trade: 'HVAC',
    type: 'HVAC CHANGEOUT',
    valueUsd: 9400,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '1044 MAITLAND AVE',
    jurisdiction: 'Altamonte Springs',
    county: 'Seminole',
    trade: 'HVAC',
    type: 'MINI-SPLIT INSTALL',
    valueUsd: 6500,
    resolved: false,
  },
  {
    date: 'AUG 31',
    address: '5010 NOVA RD',
    jurisdiction: 'St. Cloud',
    county: 'Osceola',
    trade: 'HVAC',
    type: 'NEW HVAC SYSTEM',
    valueUsd: 13400,
    resolved: false,
  },
  {
    date: 'AUG 31',
    address: '2260 SEMORAN BLVD',
    jurisdiction: 'Orlando',
    county: 'Orange',
    trade: 'ELECTRICAL',
    type: 'PANEL UPGRADE',
    valueUsd: 3900,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '2540 TUSKAWILLA RD',
    jurisdiction: 'Seminole County',
    county: 'Seminole',
    trade: 'ELECTRICAL',
    type: 'GENERATOR INTERLOCK',
    valueUsd: 3200,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '4180 CONWAY RD',
    jurisdiction: 'Orlando',
    county: 'Orange',
    trade: 'PLUMBING',
    type: 'WATER HEATER REPLACEMENT',
    valueUsd: 3400,
    resolved: true,
  },
  {
    date: 'AUG 31',
    address: '2740 MICHIGAN AVE',
    jurisdiction: 'Kissimmee',
    county: 'Osceola',
    trade: 'PLUMBING',
    type: 'WHOLE-HOME REPIPE',
    valueUsd: 11200,
    resolved: true,
    nocDaysBefore: 30,
  },
  {
    date: 'AUG 31',
    address: '10480 NARCOOSSEE RD',
    jurisdiction: 'Orange County',
    county: 'Orange',
    trade: 'POOL',
    type: 'NEW POOL AND SPA',
    valueUsd: 78500,
    resolved: true,
    nocDaysBefore: 49,
  },
  /* AUG 30 */
  {
    date: 'AUG 30',
    address: '1550 RINEHART RD',
    jurisdiction: 'Sanford',
    county: 'Seminole',
    trade: 'ROOFING',
    type: 'ROOF REPLACEMENT',
    valueUsd: 19200,
    resolved: true,
    nocDaysBefore: 38,
  },
  {
    date: 'AUG 30',
    address: '4630 KIRKMAN RD',
    jurisdiction: 'Orlando',
    county: 'Orange',
    trade: 'HVAC',
    type: 'AC REPLACEMENT',
    valueUsd: 7900,
    resolved: false,
  },
  {
    date: 'AUG 30',
    address: '9330 APOPKA VINELAND RD',
    jurisdiction: 'Orange County',
    county: 'Orange',
    trade: 'ELECTRICAL',
    type: 'SERVICE CHANGE, 200A',
    valueUsd: 5600,
    resolved: false,
    nocDaysBefore: 21,
  },
  {
    date: 'AUG 30',
    address: '1970 SIMPSON RD',
    jurisdiction: 'Osceola County',
    county: 'Osceola',
    trade: 'ELECTRICAL',
    type: 'SERVICE CHANGE, 200A',
    valueUsd: 5300,
    resolved: true,
  },
  {
    date: 'AUG 30',
    address: '915 WEKIVA SPRINGS RD',
    jurisdiction: 'Apopka',
    county: 'Orange',
    trade: 'PLUMBING',
    type: 'GAS LINE, RANGE',
    valueUsd: 3800,
    resolved: true,
  },
  {
    date: 'AUG 30',
    address: '3350 PARTIN SETTLEMENT RD',
    jurisdiction: 'Osceola County',
    county: 'Osceola',
    trade: 'POOL',
    type: 'NEW POOL AND SPA',
    valueUsd: 92000,
    resolved: true,
    nocDaysBefore: 55,
  },
  /* AUG 29 */
  {
    date: 'AUG 29',
    address: '3175 CANOE CREEK RD',
    jurisdiction: 'St. Cloud',
    county: 'Osceola',
    trade: 'ROOFING',
    type: 'ROOF REPLACEMENT',
    valueUsd: 17600,
    resolved: true,
    nocDaysBefore: 27,
  },
  {
    date: 'AUG 29',
    address: '2705 ALAFAYA TRL',
    jurisdiction: 'Oviedo',
    county: 'Seminole',
    trade: 'HVAC',
    type: 'AC REPLACEMENT',
    valueUsd: 8200,
    resolved: true,
  },
  {
    date: 'AUG 29',
    address: '611 CLARCONA OCOEE RD',
    jurisdiction: 'Ocoee',
    county: 'Orange',
    trade: 'ELECTRICAL',
    type: 'EV CHARGER CIRCUIT',
    valueUsd: 2400,
    resolved: true,
  },
  {
    date: 'AUG 29',
    address: '1105 MAGUIRE RD',
    jurisdiction: 'Ocoee',
    county: 'Orange',
    trade: 'PLUMBING',
    type: 'WHOLE-HOME REPIPE',
    valueUsd: 12500,
    resolved: false,
    nocDaysBefore: 58,
  },
  {
    date: 'AUG 29',
    address: '5290 MARDEN RD',
    jurisdiction: 'Apopka',
    county: 'Orange',
    trade: 'POOL',
    type: 'POOL RESURFACE',
    valueUsd: 21400,
    resolved: false,
  },
  /* AUG 28 */
  {
    date: 'AUG 28',
    address: '3306 LAKE UNDERHILL RD',
    jurisdiction: 'Orlando',
    county: 'Orange',
    trade: 'ROOFING',
    type: 'ROOF REPLACEMENT',
    valueUsd: 21900,
    resolved: false,
    nocDaysBefore: 33,
  },
  {
    date: 'AUG 28',
    address: '1930 ROCK SPRINGS RD',
    jurisdiction: 'Apopka',
    county: 'Orange',
    trade: 'HVAC',
    type: 'NEW HVAC SYSTEM',
    valueUsd: 12600,
    resolved: true,
    nocDaysBefore: 44,
  },
  {
    date: 'AUG 28',
    address: '3880 AIRPORT BLVD',
    jurisdiction: 'Sanford',
    county: 'Seminole',
    trade: 'ELECTRICAL',
    type: 'PANEL UPGRADE',
    valueUsd: 4100,
    resolved: false,
  },
  {
    date: 'AUG 28',
    address: '3660 MITCHELL HAMMOCK RD',
    jurisdiction: 'Oviedo',
    county: 'Seminole',
    trade: 'PLUMBING',
    type: 'WATER HEATER REPLACEMENT',
    valueUsd: 3600,
    resolved: false,
  },
  /* AUG 27 */
  {
    date: 'AUG 27',
    address: '5721 ALOMA AVE',
    jurisdiction: 'Winter Park',
    county: 'Orange',
    trade: 'ROOFING',
    type: 'RE-ROOF, TILE',
    valueUsd: 36800,
    resolved: true,
    nocDaysBefore: 52,
  },
  {
    date: 'AUG 27',
    address: '8250 HIAWASSEE RD',
    jurisdiction: 'Orange County',
    county: 'Orange',
    trade: 'HVAC',
    type: 'HVAC CHANGEOUT',
    valueUsd: 8700,
    resolved: true,
  },
  {
    date: 'AUG 27',
    address: '3715 GOLDENROD RD',
    jurisdiction: 'Orlando',
    county: 'Orange',
    trade: 'ELECTRICAL',
    type: 'NEW WIRING, ADDITION',
    valueUsd: 11800,
    resolved: true,
    nocDaysBefore: 47,
  },
  {
    date: 'AUG 27',
    address: '2035 HOWELL BRANCH RD',
    jurisdiction: 'Winter Park',
    county: 'Orange',
    trade: 'POOL',
    type: 'POOL SCREEN ENCLOSURE',
    valueUsd: 38900,
    resolved: true,
  },
  /* AUG 26 */
  {
    date: 'AUG 26',
    address: '4410 LAKE MARY BLVD',
    jurisdiction: 'Seminole County',
    county: 'Seminole',
    trade: 'ROOFING',
    type: 'RE-ROOF, METAL',
    valueUsd: 29500,
    resolved: false,
  },
  {
    date: 'AUG 26',
    address: '1420 VINE ST',
    jurisdiction: 'Kissimmee',
    county: 'Osceola',
    trade: 'HVAC',
    type: 'HVAC CHANGEOUT',
    valueUsd: 9100,
    resolved: true,
    nocDaysBefore: 36,
  },
  {
    date: 'AUG 26',
    address: '7120 COLONIAL DR',
    jurisdiction: 'Orange County',
    county: 'Orange',
    trade: 'PLUMBING',
    type: 'SEWER LINE REPAIR',
    valueUsd: 6800,
    resolved: true,
  },
  {
    date: 'AUG 26',
    address: '4415 ORANGE BLOSSOM TRL',
    jurisdiction: 'Orange County',
    county: 'Orange',
    trade: 'POOL',
    type: 'NEW POOL',
    valueUsd: 64300,
    resolved: false,
    nocDaysBefore: 24,
  },
  /* AUG 25 */
  {
    date: 'AUG 25',
    address: '2280 NEPTUNE RD',
    jurisdiction: 'Kissimmee',
    county: 'Osceola',
    trade: 'ROOFING',
    type: 'ROOF REPAIR',
    valueUsd: 9800,
    resolved: false,
  },
];

export const r407Sample: LeadSample[] = seeds.map(seed =>
  seed.resolved ? { ...seed, licenseMasked: MASK[seed.trade] } : seed
);
